import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ImagePlus, Upload } from "lucide-react";
import {
  FileDropzone, SITE_IMAGE_FILES, SITE_MEDIA_FILES, UploadProgressBar, checkUploadFile, formatBytes, from, getClientEndpoint, rpc, unwrap,
  useFileUpload, useOrg, type UploadRule,
} from "@vixio/data";
import { Button, Callout, Dialog, Select, TextInput } from "@vixio/ui";
import type { Media } from "./api";

/**
 * Mídia do site: envio em duas etapas (content.begin_media_upload → Storage → content.finish_media_upload) com o
 * componente comum de @vixio/data. O bucket content-media é público; o site serve a mídia pelo próprio domínio em
 * /media/<caminho> (proxy do nginx do site), porque o CSP do site só aceita imagens do próprio domínio.
 */

/** Caminho público no site (o mesmo de content.site_media_path). */
export function siteMediaPath(path: string): string {
  return `/media/${path}`;
}

/** URL pública no Storage (API), usada só para a prévia no painel. */
function storagePublicUrl(path: string): string | null {
  const ep = getClientEndpoint();
  return ep ? `${ep.url}/storage/v1/object/public/content-media/${path.split("/").map(encodeURIComponent).join("/")}` : null;
}

/**
 * Prévia de uma mídia já enviada. O CSP do painel não aceita imagens da API, então a imagem vem por fetch (connect-src)
 * e vira blob: (permitido em img-src).
 */
export function useMediaPreview(path: string | null | undefined, mime?: string | null): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!path || (mime && !mime.startsWith("image/"))) {
      setUrl(null);
      return;
    }
    const src = storagePublicUrl(path);
    if (!src) return;
    let alive = true;
    let objectUrl: string | null = null;
    fetch(src)
      .then((r) => (r.ok ? r.blob() : null))
      .then((b) => {
        if (!alive || !b) return;
        objectUrl = URL.createObjectURL(b);
        setUrl(objectUrl);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [path, mime]);
  return url;
}

export function MediaThumb({ media, size = 48 }: { media: Pick<Media, "path" | "mime_type" | "alt_text">; size?: number }) {
  const url = useMediaPreview(media.path, media.mime_type);
  const box = { width: size, height: size, borderRadius: 4, border: "1px solid var(--border)", background: "var(--surface-2)", flexShrink: 0 } as const;
  return url ? <img src={url} alt={media.alt_text ?? ""} style={{ ...box, objectFit: "cover" }} /> : <span aria-hidden="true" style={{ ...box, display: "inline-block" }} />;
}

/** Dimensões de uma imagem escolhida (para o registro e o layout do site); null se não for imagem legível. */
async function imageSize(file: File): Promise<{ width: number; height: number } | null> {
  if (!file.type.startsWith("image/") && !/\.(png|jpe?g|webp|gif|avif)$/i.test(file.name)) return null;
  try {
    const bmp = await createImageBitmap(file);
    const r = { width: bmp.width, height: bmp.height };
    bmp.close();
    return r;
  } catch {
    return null;
  }
}

export interface UploadedMedia {
  media_id: string;
  path: string;
  site_path: string;
}

/** Diálogo de envio: escolher/arrastar, prévia, texto alternativo (obrigatório em imagem), legenda e crédito. */
export function MediaUploadDialog({ rule = SITE_MEDIA_FILES, title = "Enviar mídia do site", onClose, onUploaded }: {
  rule?: UploadRule;
  title?: string;
  onClose: () => void;
  onUploaded?: (m: UploadedMedia) => void;
}) {
  const org = useOrg();
  const qc = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [alt, setAlt] = useState("");
  const [caption, setCaption] = useState("");
  const [credit, setCredit] = useState("");
  const [pickError, setPickError] = useState<string | null>(null);
  const isImage = !!file && !/\.pdf$/i.test(file.name);
  const up = useFileUpload({
    rule,
    begin: (contentType, f) => rpc("content", "begin_media_upload", { p_org: org.id, p_name: f.name, p_content_type: contentType, p_size: f.size }),
    finish: async (t, f) => {
      const dim = await imageSize(f);
      return rpc("content", "finish_media_upload", {
        p_upload: t.upload_id, p_alt_text: alt.trim() || null, p_caption: caption.trim() || null, p_credit: credit.trim() || null,
        p_width: dim?.width ?? null, p_height: dim?.height ?? null,
      });
    },
    onDone: (r) => {
      void qc.invalidateQueries({ queryKey: ["content"] });
      onUploaded?.(r);
      onClose();
    },
  });
  useEffect(() => {
    if (!file || !isImage) {
      setPreview(null);
      return;
    }
    const u = URL.createObjectURL(file);
    setPreview(u);
    return () => URL.revokeObjectURL(u);
  }, [file, isImage]);
  const altOk = !isImage || alt.trim().length >= 3;
  const busy = up.status === "uploading";
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && !busy && onClose()}
      title={title}
      description="O arquivo fica no armazenamento público do site e aparece no site pelo próprio domínio (/media/…)."
      onSubmit={() => file && altOk && !busy && void up.start(file)}
      footer={
        <>
          <Button onClick={busy ? up.cancel : onClose}>{busy ? "Cancelar envio" : "Cancelar"}</Button>
          <Button type="submit" variant="primary" icon={<Upload />} loading={busy} disabled={!file || !altOk || !!pickError}>
            Enviar
          </Button>
        </>
      }
    >
      <div className="vx-stack">
        {busy && file ? (
          <UploadProgressBar name={file.name} progress={up.progress} />
        ) : file ? (
          <div className="vx-filepick vx-filepick--chosen" style={{ gap: 12 }}>
            {preview ? <img src={preview} alt="" style={{ width: 72, height: 72, objectFit: "cover", borderRadius: 4 }} /> : null}
            <div style={{ minWidth: 0, flex: 1 }}>
              <p className="vx-filepick__name">{file.name}</p>
              <p className="vx-filepick__meta">{pickError ?? formatBytes(file.size)}</p>
            </div>
            <Button size="sm" variant="ghost" onClick={() => { setFile(null); setPickError(null); }}>
              Trocar
            </Button>
          </div>
        ) : (
          <FileDropzone
            rule={rule}
            label="Arquivo"
            testId="midia-arquivo"
            onPick={(f) => {
              setPickError(checkUploadFile(f, rule));
              setFile(f);
            }}
            error={pickError}
          />
        )}
        {up.status === "error" ? <Callout tone="crit">{up.error}</Callout> : null}
        <TextInput
          label="Texto alternativo"
          hint="Descreva a imagem para quem não a vê (leitores de tela e buscadores). Ex.: Equipe reunida em volta de um quadro."
          value={alt}
          onChange={setAlt}
          maxLength={300}
          required={isImage}
          disabled={busy}
        />
        <TextInput label="Legenda (opcional)" value={caption} onChange={setCaption} maxLength={500} disabled={busy} />
        <TextInput label="Crédito (opcional)" hint="Fonte da imagem, quando não for da Vixio." value={credit} onChange={setCredit} maxLength={300} disabled={busy} />
      </div>
    </Dialog>
  );
}

/** Lista da mídia da organização (imagens), para escolher capa e imagem de compartilhamento. */
export function useOrgMedia(orgId: string) {
  return useQuery({
    queryKey: ["content", "media", orgId],
    queryFn: () => unwrap<Media[]>(from("content", "media").select("*").eq("org_id", orgId).order("created_at", { ascending: false })),
  });
}

/** Escolha de uma imagem já enviada, ou envio de uma nova (fica escolhida). */
export function MediaPicker({ label, value, onChange, disabled }: { label: string; value: string | null; onChange: (id: string | null) => void; disabled?: boolean }) {
  const org = useOrg();
  const media = (useOrgMedia(org.id).data ?? []).filter((m) => m.mime_type.startsWith("image/"));
  const [uploading, setUploading] = useState(false);
  const current = media.find((m) => m.id === value) ?? null;
  return (
    <div className="vx-stack" style={{ gap: 6 }}>
      <div className="vx-row" style={{ gap: 8, alignItems: "flex-end", flexWrap: "nowrap" }}>
        {current ? <MediaThumb media={current} /> : null}
        <div style={{ flex: 1, minWidth: 0 }}>
          <Select
            label={label}
            value={value ?? ""}
            onChange={(v) => onChange(v || null)}
            disabled={disabled}
            options={[{ value: "", label: "Sem imagem" }, ...media.map((m) => ({ value: m.id, label: `${m.alt_text ?? m.path.split("/").pop()} (${m.path.split("/").pop()})` }))]}
          />
        </div>
        {disabled ? null : (
          <Button icon={<ImagePlus />} onClick={() => setUploading(true)}>
            Enviar imagem
          </Button>
        )}
      </div>
      {uploading ? (
        <MediaUploadDialog
          rule={SITE_IMAGE_FILES}
          title={`Enviar imagem: ${label.toLocaleLowerCase("pt-BR")}`}
          onClose={() => setUploading(false)}
          onUploaded={(m) => onChange(m.media_id)}
        />
      ) : null}
    </div>
  );
}
